import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useMemo } from 'react';
import {
    AccessoriesPageData,
} from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { Filter } from 'src/utils/composeFilter.ts';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { Accessory } from 'src/common/Accessory/Accessory';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { LocationState } from 'src/modules/LocationState.ts';
import { GetQty, parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { isGatewayAccessory, isRegularAccessory, isToolsAccessory } from 'src/modules/common/AccessoryFilters.ts';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import { ValveAccessoriesPipeConnectors } from 'src/modules/accessories/components/ValveAccessoriesPipeConnectors';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';

export const AccessoriesPage = ({
    data,
    page,
    filter,
    searchParams,
    valveLabel,
    actuatorLabel,
}: {
    data: AccessoriesPageData;
    filter: Filter;
    searchParams: URLSearchParams;
    page: string;
    valveLabel: string;
    actuatorLabel: string;
}) => {
    const { isMobile } = useContext(RuntimeContext);

    const { state: userInputState, locationState, setLocationState } = useUserInputState([]);

    const onChange = useCallback((label: string, getQty: GetQty) => {
        let selectedAccessories: LocationState['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({ ...locationState, selectedAccessories });
    }, [locationState, setLocationState]);

    const {
        isRecommended,
        getQty,
    } = parseRecommended(data.vssAccessories.valve.connectors);

    const onPipeConnectorsChange = useCallback((value: boolean) => {
        setLocationState({
            ...locationState,
            pipeConnectors: value,
            selectedAccessories: [
                ...locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
                ...(value ? data.vssAccessories.valveAccessories
                    .filter(({ item }) => isRecommended(item.Label))
                    .map(({ item }) => ({ label: item.Label, qty: getQty(item.Label) }))
                    : []),
            ],
        });
    }, [setLocationState, locationState, data, isRecommended, getQty]);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const nextLocationState = useMemo(() => ({
        ...locationState,
        selectedAccessories: [
            ...locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
            ...(locationState.pipeConnectors ? data.vssAccessories.valveAccessories
                .filter(({ item }) => isRecommended(item.Label))
                .map(({ item }) => ({ label: item.Label, qty: getQty(item.Label) }))
                : []),
        ],
    }), [locationState, data.vssAccessories.valveAccessories, isRecommended, getQty]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="forward"
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 5 - add to schedule',
                    eventLabel: locationState.selectedAccessories.length > 0
                        ? 'add combo and accessories to schedule' : 'add to schedule',
                }}
                state={nextLocationState}
                to={{
                    pathname: [
                        PATHS.Checkout,
                        encodeURIComponent(valveLabel),
                        encodeURIComponent(actuatorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const renderAccessory = (
        item: AccessoriesPageData['vssAccessories']['valveAccessories'][0]['item'],
    ) => (
        <Accessory
            chip={isRecommended(item.Label) ? <I18n messageId={MESSAGES.recommended}/> : null}
            accessory={item}
            onSelect={() => onChange(item.Label, getQty)}
            selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
            key={`${item.Label}-${item.ID}`}
        />
    );

    const pipeConnectors = data.vssAccessories.valveAccessories
        .filter(({ item }) => isRecommended(item.Label))
        .sort(sortAccessoriesBy(isRecommended));

    const valveAccessories = data.vssAccessories.valveAccessories
        .filter(({ item }) => !isRecommended(item.Label));

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <ValveBreadcrumbs
                            currentPage={PATHS.Accessories}
                            homeValue={homePageValue}
                            userInputState={userInputState}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={(
                        <SelectionsButton searchParams={searchParams} locationState={locationState}/>
                    )}
                />
                {(data.vssAccessories.valveAccessories.length === 0 &&
                (data.vssAccessories.actuatorAccessories?.length || 0) === 0) && (<NoData/>)}
                <div className="vss-accessories-page">
                    {pipeConnectors.length > 0 && (
                        <ValveAccessoriesPipeConnectors
                            locationState={locationState}
                            pipeConnectors={pipeConnectors}
                            onPipeConnectorsChange={onPipeConnectorsChange}
                            getQty={getQty}
                        />
                    )}
                    {valveAccessories && valveAccessories.filter(isRegularAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageValvesTitle}>
                            {valveAccessories.filter(isRegularAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveAccessories && valveAccessories.filter(isGatewayAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageGateways}>
                            {valveAccessories.filter(isGatewayAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveAccessories && valveAccessories.filter(isToolsAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageTools}>
                            {valveAccessories.filter(isToolsAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveLabel !== actuatorLabel &&
                        data.vssAccessories.actuatorAccessories &&
                        data.vssAccessories.actuatorAccessories.filter(isRegularAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageActuatorTitle}>
                            {data.vssAccessories.actuatorAccessories.filter(isRegularAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveLabel !== actuatorLabel &&
                        data.vssAccessories.actuatorAccessories &&
                        data.vssAccessories.actuatorAccessories.filter(isGatewayAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageGateways}>
                            {data.vssAccessories.actuatorAccessories.filter(isGatewayAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveLabel !== actuatorLabel &&
                        data.vssAccessories.actuatorAccessories &&
                        data.vssAccessories.actuatorAccessories.filter(isToolsAccessory).length > 0 && (
                        <AccessoryGrid title={MESSAGES.accessoriesPageTools}>
                            {data.vssAccessories.actuatorAccessories.filter(isToolsAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}
                </div>

            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
